import React, { Component } from 'react';
import axios from 'axios';
import csv from 'csvtojson';
import {
  Header, Footer, IllustratedHeader, ColoredHeading, CompHeading
} from '@massds/mayflower-react';

import Table from './components/Table';
import DeltaTripsSection from './sections/DeltaTripsSection';
import TotalTripsSection from './sections/TotalTripsSection';
import FlowSection from './sections/FlowSection';
import SpeedSection from './sections/SpeedSection';
import FootNote from './components/FootNote';
import FootNoteLink from './components/FootNote/FootNoteLink';

import image from './media/shutterstock_629908583.jpg';

import masstowns from './data/masstowns.topo.json';
import UtilityNavData from './data/UtilityNav.data';
import MainNavData from './data/MainNav.data';
import HeaderSearchData from './data/HeaderSearch.data';
import FooterLinksLiveData from './data/FooterLinksLive.json';
import SocialLinksLiveData from './data/SocialLinksLive.json';
import { mapAreas } from './sections/mapOptions.data';

import './App.css';

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      allData: []
    };
    /* eslint-enable react/no-unused-state */
    this.footerProps = {
      footerLinks: FooterLinksLiveData.footerLinks,
      socialLinks: SocialLinksLiveData.socialLinks
    };
    this.headerProps = {
      utilityNav: UtilityNavData,
      headerSearch: HeaderSearchData,
      mainNav: MainNavData,
      hideHeaderSearch: true,
      hideBackTo: true,
      siteLogoDomain: { url: { domain: 'https://www.mass.gov/' } }
    };
    this.illustratedHeader = {
      bgInfo: 'rideshare placeholder image',
      bgImage: image,
      category: '2018 Data Report',
      inverted: false,
      publishState: {
        text: ''
      },
      pageHeader: {
        title: 'Rideshare in Massachusetts',
        subTitle: '',
        optionalContents: [{
          paragraph: {
            text: 'In 2018, rideshare companies, also called transportation network companies (TNCs), provided <b>81.3 million</b> rides in Massachusetts, approximately 25% more than in 2017. This increase happened across the state, in towns and cities of all sizes and types.<br /><br />Massachusetts law requires rideshare companies to share data with the Commonwealth. The companies also pay an assessment, 20 cents per ride, that is distributed among cities and towns, Massachusetts’s general transportation fund, and the taxi and livery industry. In 2017, these fees amounted to more than $12 million, and in 2018, more than $16 million. <a href="https://www.mass.gov/info-details/2017-municipal-rideshare-fund-report">See how your municipality allocated the funds</a> it received last year.<br /><br />This report includes an interactive summary of the 2018 rideshare data. You can explore how people in your city or town use rideshare services, such as how many rides began where you live and where they ended. You’ll also learn how population density and other factors affect ridesharing. You can also <a href="./data/TNC_Dataset_2018_for_download.xlsx">download the source data as an Excel spreadsheet</a> or <a href="https://tnc.sites.digital.mass.gov/2017/">view the 2017 report.</a>'
          }
        }],
        headerTags: null,
        publishState: null
      }
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    const _this = this;
    axios.all([axios.get('./data/RIDESHARE_STATS_2018.csv')])
      .then(axios.spread((result) => {
        const { data } = result;
        _this.parseData(data, 'allData');
      }))
      .catch((error) => { console.log(error); });
    axios.all([axios.get('../data/RIDESHARE_MATRIX_2018.csv')])
      .then(axios.spread((result) => {
        const { data } = result;
        _this.parseData(data, 'matrixData');
      }))
      .catch((error) => { console.log(error); });
    axios.all([axios.get('../data/RIDESHARE_MATRIX_2018_TO.csv')])
      .then(axios.spread((result) => {
        const { data } = result;
        _this.parseData(data, 'matrixDataTransposed');
      }))
      .catch((error) => { console.log(error); });
  };

  parseData = (data, name) => {
    const parsedData = [];
    csv({
      noheader: false
    }).fromString(data)
      .subscribe((json) => {
        parsedData.push(json);
      })
      .on('done', () => {
        this.setState({ [name]: parsedData });
      })
      .on('error', (err) => {
        console.log(err);
      });
  }

  render() {
    const { allData, matrixData, matrixDataTransposed } = this.state;
    return(
      <div className="App">
        <Header {...this.headerProps} />
        <IllustratedHeader {...this.illustratedHeader}>
          <hr />
          Data Source:
          <a href="https://www.mass.gov/orgs/department-of-public-utilities"><span className="credit"> Department of Public Utilities (DPU)</span></a>
        </IllustratedHeader>
        <main id="main-content" className="main-content">
          <section className="main-content--two">
            <ColoredHeading text="2018 Growth in Ridesharing Across Massachusetts" color="blue" centered />
            <p>
              In 2018, there were about 81.3 million TNC rides
              <FootNoteLink index={1} />
              in Massachusetts  – 25% more than the 64.8 million in 2017.
              This increase happened throughout the state. The largest increases in numbers of rides from  2017 to 2018 happened in Boston (7.3 million more), Cambridge (1 million more), and Somerville (almost 600,000 more).
              <br />
              <br />
              However, many smaller towns with smaller numbers of rides saw a much larger percentage increase in rides when compared to 2017 ridership. For example, in Provincetown, ridesharing companies reported 48,862 rides in 2018, or 181% more rides than 2017. The data shows similar growth for Ayer (5,848 rides in 2018 and +172% growth), Pittsfield (27,189 rides and +167% growth), and New Bedford (164,937 rides and +155% growth).
            </p>
            {allData && (
            <DeltaTripsSection
              allData={allData}
              geography={masstowns}
              areas={mapAreas}
            />
            )}
            <ColoredHeading text="Total Rides and Rides Per Person in 2018" color="blue" centered />
            <p>
              There were approximately 7 billion household passenger vehicle trips in 2018 across Massachusetts
              <FootNoteLink index={6} />
              . While 81.3 million rideshares make up a small percentage of overall passenger car trips, the data suggests that TNC use has become an important thread in the fabric of Massachusetts transportation, especially when it comes to getting around cities and to the airport. For example:
            </p>
            <ul>
              <li>80% of all TNC rides in Massachusetts began in Suffolk and Middlesex Counties</li>
              <li>42.2 million rides began in Boston  – the most of any city or town. Cambridge, where 7.8 million rides began, is second on this list.</li>
              <li>
                TNC rides to and from Boston Logan International Airport totaled approximately 7 million
                <FootNoteLink index={3} />
                , and are included in the total number of rides in Boston.
                There were almost 4.1 million TNC rides to the Airport, and 2.9 million TNC rides leaving the Airport.
              </li>
              <li>More people used ridesharing to leave urban centers like Cambridge, Somerville, Brookline, Springfield, and Worcester than to enter them. (This is true for Boston, too, when we remove the airport.)</li>
              <li>
                For reference, there were 397.7 million public transit rides in 2018.
                <FootNoteLink index={2} />
              </li>
            </ul>
            {allData && (
              <TotalTripsSection
                allData={allData}
                geography={masstowns}
                areas={mapAreas}
              />
            )}
            <CompHeading title="Table of Total Rides Started and Rides Started Per Person by Municipality" level={4} centered />
            <p>Use this table to explore the TNC rideshare data. You can sort by column headings, or use the search bar to look for a town or city you’re interested in.</p>
            {allData && allData.length > 0
              && (
              <Table
                id="municipality"
                data={allData}
                headRow="TOWN"
                pageSize={10}
                pagination
              />
              )
            }
          </section>
          <section className="main-content--two">
            {matrixData && matrixDataTransposed && (
              <FlowSection
                allData={{ matrixData, matrixDataTransposed, fullData: allData }}
                geography={masstowns}
                defaultTown="BOSTON"
                defaultCentroid={[-71.10457763, 42.30844048]}
                areas={mapAreas}
                showZoom
              />
            )}
          </section>
          <section className="main-content--two">
            {allData && (
              <SpeedSection
                allData={allData}
                geography={masstowns}
                areas={mapAreas}
              />
            )}
          </section>
        </main>
        <FootNote />
        <Footer {...this.footerProps} />
      </div>
    );
  }
}

export default App;
